import React, { useState, useEffect, useMemo } from 'react';
import { asService } from '../services/asService';
import { recallService } from '../services/recallService';
import { noticeService } from '../services/noticeService';
import { customerService } from '../services/customerService';
import { partService } from '../services/partService';

// 分리한 유틸리티 불러오기
import { getLocalDateString, downloadDailyReport, printAdvancedReport } from '../utils/dashboardUtils';

const LOW_STOCK_THRESHOLD = 3;

const Dashboard = ({ user, onMenuClick }) => {
  const [sales, setSales] = useState({ daily: 0, monthly: 0, yearly: 0 });
  const [asStatus, setAsStatus] = useState({ total: 0, pending: 0, done: 0 });
  const [customerStatus, setCustomerStatus] = useState({ total: 0, new: 0 });
  const [recallSummary, setRecallSummary] = useState([]);
  const [notices, setNotices] = useState([]);
  const [partsValue, setPartsValue] = useState({ total: 0, dailyChange: 0, byModel: {} });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ type: '', title: '', content: '' });
  const [rates, setRates] = useState({ USD: '0', CNY: '0', EUR: '0' });

  const [dailyAsList, setDailyAsList] = useState([]);
  const [allCompletedAs, setAllCompletedAs] = useState([]); 
  const [allPartsRaw, setAllPartsRaw] = useState([]);
  
  // 주간 예약 현황을 담을 상태값
  const [weeklyReservations, setWeeklyReservations] = useState([]);

  const fetchRates = async () => {
    try {
      const res = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await res.json();
      if (data && data.rates) {
        const usdToKrw = data.rates.KRW;
        setRates({ USD: usdToKrw.toFixed(2), CNY: (usdToKrw / data.rates.CNY).toFixed(2), EUR: (usdToKrw / data.rates.EUR).toFixed(2) });
      }
    } catch (err) { console.error("환율 로드 실패", err); }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [allAs, allRecall, allNotices, allCust, allParts] = await Promise.all([
        asService.getRecords(), recallService.getRecallExecutions(), noticeService.getNotices(),
        customerService.getCustomers(), partService.getParts()
      ]);

      // 외부 열람 계정에게는 고객 이름을 가린다(이 목록에서 파생되는 모든 표·출력에 적용).
      const safeAllAs = allAs || []; // 이름 마스킹은 서비스 계층에서 처리됨
      const safeAllRecall = allRecall || [];
      const safeAllNotices = allNotices || [];
      const safeAllCust = allCust || [];
      const safeAllParts = allParts || [];

      const today = getLocalDateString();
      const thisMonth = today.substring(0, 7);
      const thisYear = today.substring(0, 4);

      // 오늘부터 7일간의 데이터 범위 계산
      const todayDate = new Date();
      const nextWeekDate = new Date();
      nextWeekDate.setDate(todayDate.getDate() + 6); // 오늘 포함 7일

      const formatDateObj = (dateObj) => {
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        const d = String(dateObj.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`; 
      };

      // 타겟 탐색 스트링 생성
      const y1 = todayDate.getFullYear();
      const m1 = String(todayDate.getMonth() + 1).padStart(2, '0');
      const d1 = String(todayDate.getDate()).padStart(2, '0');
      const todayStr = `${y1}-${m1}-${d1}`;

      const y2 = nextWeekDate.getFullYear();
      const m2 = String(nextWeekDate.getMonth() + 1).padStart(2, '0');
      const d2 = String(nextWeekDate.getDate()).padStart(2, '0');
      const nextWeekStr = `${y2}-${m2}-${d2}`;

      const reservations = safeAllAs.filter(r =>
        (r.status === '예약' || r.status === '접수') && r.asDate >= todayStr && r.asDate <= nextWeekStr
      ).sort((a, b) => a.asDate.localeCompare(b.asDate));

      setWeeklyReservations(reservations);

      let dSales = 0, mSales = 0, ySales = 0, done = 0;
      let dList = []; 
      const usedPartsMap = {};

      const completedAs = safeAllAs.filter(r => r.status === '완료');
      setAllCompletedAs(completedAs);

      completedAs.forEach(r => {
        // 🚀 대시보드는 이미 순수 공급가(costExVat) 기준으로 훌륭하게 합산하고 있었습니다!
        const costWithVat = (Number(r.totalCost) || 0) + (Number(r.discount) || 0); // 할인 전 총액 기준으로 공급가 보정
        const costExVat = Math.round(costWithVat / 1.1);
        const date = r.settleDate || r.asDate;

        if (r.parts && Array.isArray(r.parts)) {
          r.parts.forEach(p => { 
            usedPartsMap[p.taskName] = (usedPartsMap[p.taskName] || 0) + (Number(p.qty) || 0); 
          });
        }

        if (date === today) { dSales += costExVat; dList.push({ ...r, costExVat }); }
        if (date?.startsWith(thisMonth)) mSales += costExVat;
        if (date?.startsWith(thisYear)) ySales += costExVat;
        done++;
      });

      let totalV = 0;
      const modelVMap = {};
      safeAllParts.forEach(p => {
        const value = (Number(p.stock) || 0) * (Number(p.purchasePrice) || 0);
        totalV += value;
        modelVMap[p.modelName || '공용부품'] = (modelVMap[p.modelName || '공용부품'] || 0) + value;
      });

      const enrichedParts = safeAllParts.map(p => {
        const currentStock = Number(p.stock) || 0;
        const usedQty = usedPartsMap[p.partName] || 0;
        return { ...p, initialStock: currentStock + usedQty, usedQty, currentStock, value: currentStock * (Number(p.purchasePrice) || 0) };
      });

      setSales({ daily: dSales, monthly: mSales, yearly: ySales });
      setDailyAsList(dList); 
      setAllPartsRaw(enrichedParts);
      
      setAsStatus({ total: safeAllAs.length, pending: safeAllAs.length - done, done });
      setCustomerStatus({ 
        total: safeAllCust.length, 
        new: safeAllCust.filter(c => {
          const cDate = c.createdAt?.seconds ? getLocalDateString(new Date(c.createdAt.seconds * 1000)) : (c.createdAt || "").split('T')[0];
          return cDate === today;
        }).length 
      });
      
      setRecallSummary(safeAllRecall.slice(0, 3));
      setPartsValue({ total: totalV, dailyChange: 0, byModel: modelVMap });
      setNotices(safeAllNotices.slice(0, 8)); 

    } catch (error) { console.error("데이터 로드 실패:", error); }
    setLoading(false);
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      fetchDashboardData();
      fetchRates();
    }
    return () => {
      isMounted = false;
    };
  }, []);

  const handleNoticeClick = (n) => {
    setModalData({ type: 'NOTICE', title: n.title, content: n.content || '내용 없음' });
    setShowModal(true);
  };

  const lowStockParts = useMemo(() => {
    return allPartsRaw.filter(p => Number(p.currentStock) <= LOW_STOCK_THRESHOLD)
                      .sort((a, b) => Number(a.currentStock) - Number(b.currentStock));
  }, [allPartsRaw]);

  const lowStockSummary = useMemo(() => {
    const summary = {};
    lowStockParts.forEach(p => {
      const model = p.modelName || '공용부품';
      summary[model] = (summary[model] || 0) + 1;
    });
    return summary;
  }, [lowStockParts]);

  const downloadLowStockReport = () => {
    const sortedList = [...lowStockParts].sort((a, b) => {
      const modelA = a.modelName || '공용부품'; const modelB = b.modelName || '공용부품';
      return modelA === modelB ? (a.partName || '').localeCompare(b.partName || '') : modelA.localeCompare(modelB);
    });
    const tableData = sortedList.map(p => [p.modelName || '공용부품', `${p.partNo || '-'}\n${p.partName || '알 수 없음'}`, `₩ ${(Number(p.purchasePrice) || 0).toLocaleString()}`, p.currentStock === 0 ? '<span class="highlight-red">품절 (0 EA)</span>' : `<span style="color:#f59e0b; font-weight:bold">${p.currentStock} EA</span>`]);
    printAdvancedReport('🚨 차종별 긴급 재고 부족 리포트', ['모델', '부품정보', '단가(원가)', '현재고'], tableData, `총 부족 건수: ${sortedList.length} 건`);
  };

  const styles = {
    wrapper: { padding: '20px 50px', background: '#f4f7f9', minHeight: '100vh' },
    mainLayout: { display: 'grid', gridTemplateColumns: '2.2fr 1fr', gap: '20px' },
    leftCol: { display: 'flex', flexDirection: 'column', gap: '15px' },
    rowCard: { background: 'white', padding: '15px 20px', borderRadius: '15px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    miniSalesCard: (color) => ({ background: 'white', padding: '12px 15px', borderRadius: '12px', borderLeft: `6px solid ${color}`, boxShadow: '0 2px 10px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }),
    title: { fontSize: '14px', fontWeight: 'bold', color: '#1e293b', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    contentCenter: { flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' },
    scrollBox: { maxHeight: '80px', overflowY: 'auto' }, 
    sideCard: { background: 'white', padding: '20px', borderRadius: '15px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' },
    statusVal: { fontSize: '20px', fontWeight: '900' },
    subLabel: { fontSize: '10px', color: '#94a3b8' },
    btnDownload: { padding: '4px 8px', fontSize: '10px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  };

  return (
    <div style={styles.wrapper}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: '900', margin: 0 }}>📊 E2B 실시간 경영 대시보드</h2>
        <div style={{ display: 'flex', gap: '15px', fontSize: '13px', color: '#64748b' }}>
          <span>USD <b>{rates.USD}</b></span><span>CNY <b>{rates.CNY}</b></span><span>EUR <b>{rates.EUR}</b></span>
        </div>
      </div>

      <div style={styles.mainLayout}>
        <div style={styles.leftCol}>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', height: '100px' }}>
            <div style={styles.miniSalesCard('#3b82f6')}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: '#64748b', fontSize: '11px', fontWeight: 'bold' }}>오늘 매출 (공급가)</div>
                <button onClick={() => downloadDailyReport(dailyAsList, sales.daily)} style={styles.btnDownload}>명세서</button>
              </div>
              <div style={{ fontSize: '18px', fontWeight: '900', marginTop: '8px' }}>₩{sales.daily.toLocaleString()}</div>
            </div>
            <div style={styles.miniSalesCard('#10b981')}>
              <div style={{ color: '#64748b', fontSize: '11px', fontWeight: 'bold' }}>이달 누적 (공급가)</div>
              <div style={{ fontSize: '18px', fontWeight: '900', marginTop: '8px' }}>₩{sales.monthly.toLocaleString()}</div>
            </div>
            <div style={{...styles.miniSalesCard('#f59e0b'), borderRight: 'none'}}>
              <div style={{ color: '#64748b', fontSize: '11px', fontWeight: 'bold' }}>연간 매출 (공급가)</div>
              <div style={{ fontSize: '18px', fontWeight: '900', marginTop: '8px' }}>₩{sales.yearly.toLocaleString()}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div style={{ ...styles.rowCard, height: '110px' }}>
              <div style={styles.title}>🛠️ AS 처리 <span onClick={()=>onMenuClick('as')} style={{fontSize:'10px', color:'#3b82f6', cursor:'pointer'}}>more</span></div>
              <div style={styles.contentCenter}>
                <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                  <div><div style={styles.statusVal}>{asStatus.total}</div><div style={styles.subLabel}>총접수</div></div>
                  <div><div style={{...styles.statusVal, color:'#ea580c'}}>{asStatus.pending}</div><div style={styles.subLabel}>진행</div></div>
                  <div><div style={{...styles.statusVal, color:'#16a34a'}}>{asStatus.done}</div><div style={styles.subLabel}>완료</div></div>
                </div>
              </div>
            </div>
            <div style={{ ...styles.rowCard, height: '110px' }}>
              <div style={styles.title}>👥 고객 지표 <span onClick={()=>onMenuClick('customer')} style={{fontSize:'10px', color:'#3b82f6', cursor:'pointer'}}>more</span></div>
              <div style={styles.contentCenter}>
                <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                  <div><div style={styles.statusVal}>{customerStatus.total}명</div><div style={styles.subLabel}>전체고객</div></div>
                  <div><div style={{...styles.statusVal, color:'#2563eb'}}>{customerStatus.new}명</div><div style={styles.subLabel}>신규(오늘)</div></div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '20px' }}>
            <div style={{ ...styles.rowCard, height: '130px' }}>
              <div style={styles.title}>📦 부품 자산 (₩{partsValue.total.toLocaleString()})</div>
              <div style={styles.contentCenter}>
                <div style={styles.scrollBox}>
                  <table style={{width:'100%', fontSize:'12px'}}>
                    <tbody>
                      {Object.entries(partsValue.byModel).map(([m, v]) => (
                        <tr key={m} style={{borderBottom:'1px solid #f8fafc'}}>
                          <td style={{padding:'4px 0'}}>{m}</td>
                          <td style={{textAlign:'right', fontWeight:'bold'}}>₩{v.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div style={{ ...styles.rowCard, height: '130px' }}>
              <div style={styles.title}>📢 리콜 이행</div>
              <div style={styles.contentCenter}>
                <div style={{...styles.scrollBox, maxHeight: '70px'}}>
                  {recallSummary.map(r => {
                    const targets = r.targets || []; const pct = targets.length > 0 ? Math.round((targets.filter(t => t.status === '완료').length / targets.length) * 100) : 0;
                    return (<div key={r.id} style={{ marginBottom: '8px' }}><div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}><span>{r.title}</span><b>{pct}%</b></div><div style={{width:'100%', height:'4px', background:'#eee', borderRadius:'2px', marginTop:'2px'}}><div style={{width:`${pct}%`, height:'100%', background:'#3b82f6', borderRadius:'2px'}} /></div></div>);
                  })}
                </div>
              </div>
            </div>
          </div>

          <div style={{ ...styles.rowCard, flex: 1, minHeight: '170px' }}>
            <div style={styles.title}>📅 주간 AS 예약 및 대기 현황 (오늘 ~ 7일) <span onClick={()=>onMenuClick('as')} style={{fontSize:'10px', color:'#3b82f6', cursor:'pointer'}}>캘린더 보기</span></div>
            <div style={{ flex: 1, overflowY: 'auto', background: '#f8fafc', borderRadius: '10px', padding: '10px' }}>
              {weeklyReservations.length > 0 ? (
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: '#64748b', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                      <th style={{ paddingBottom: '8px', width: '15%' }}>예약일</th>
                      <th style={{ paddingBottom: '8px', width: '12%' }}>구분</th>
                      <th style={{ paddingBottom: '8px', width: '25%' }}>고객명 (차량)</th>
                      <th style={{ paddingBottom: '8px', width: '33%' }}>예약 내용 / 증상</th>
                      <th style={{ paddingBottom: '8px', width: '15%' }}>담당자</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyReservations.map(r => (
                      <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 0', fontWeight: 'bold', color: '#9333ea' }}>{r.asDate}</td>
                        <td>
                          <span style={{ 
                            fontSize: '10px', 
                            padding: '2px 6px', 
                            borderRadius: '4px', 
                            fontWeight: 'bold',
                            background: r.status === '접수' ? '#e0f2fe' : '#f3e8ff',
                            color: r.status === '접수' ? '#0369a1' : '#6b21a8'
                          }}>
                            {r.status}
                          </span>
                        </td>
                        <td style={{ fontWeight: 'bold' }}>{r.customerName} <span style={{color: '#94a3b8', fontWeight: 'normal'}}>{r.carNumber && `(${r.carNumber})`}</span></td>
                        <td style={{ color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '220px' }}>{r.symptom || '일반 점검 예약'}</td>
                        <td>{r.mechanic || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ textAlign: 'center', color: '#94a3b8', marginTop: '20px', fontSize: '12px' }}>일주일간 예정된 예약이 없습니다.</div>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div style={{...styles.sideCard, height: '210px', borderTop: '6px solid #ef4444'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <div style={{...styles.title, fontSize: '15px', color: '#ef4444', margin: 0}}>⚠️ 차종별 재고 부족 알림</div>
              <button onClick={downloadLowStockReport} style={{...styles.btnDownload, background: '#ef4444', padding: '6px 10px'}}>전체 상세 출력</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '5px' }}>
              {Object.keys(lowStockSummary).length > 0 ? (
                Object.entries(lowStockSummary).map(([model, count]) => (
                  <div key={model} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fef2f2', borderRadius: '10px', border: '1px solid #fecaca' }}>
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#991b1b' }}>{model}</span>
                    <span style={{ fontSize: '15px', fontWeight: '900', color: '#ef4444' }}>{count}건 부족</span>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', marginTop: '40px' }}>부족한 부품이 없습니다. 🎉</div>
              )}
            </div>
          </div>

          <div style={{...styles.sideCard, flex: 1, borderTop: '6px solid #8e0a2d', position: 'relative', zIndex: 10}}>
            <div style={{...styles.title, fontSize: '15px', marginBottom: '10px'}}>📌 사내 공지사항</div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {notices.map(n => (
                <div 
                  key={n.id} 
                  style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', position: 'relative', zIndex: 11 }} 
                  onClick={() => handleNoticeClick(n)}
                >
                  <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '3px' }}>{n.isImportant && <span style={{color:'red'}}>[중요] </span>}{n.title}</div>
                  <div style={{ fontSize: '10px', color: '#94a3b8' }}>{n.regDate || n.date}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }} onClick={() => setShowModal(false)}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '20px', width: '450px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, borderBottom: '1px solid #eee', paddingBottom: '10px' }}>{modalData.title}</h3>
            <div style={{ margin: '20px 0', lineHeight: '1.6', whiteSpace: 'pre-wrap', color: '#475569' }}>{modalData.content}</div>
            <button onClick={() => setShowModal(false)} style={{ width:'100%', padding: '12px', background: '#1e293b', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' }}>확인</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;